/**
 * What has to happen before a page is left.
 *
 * Navigation used to be expressed only as "click this page's Back button", so
 * leaving from a nested page walked up one step at a time — asking a separate
 * question at each one — and never arrived anywhere the walk did not lead.
 * Clicking Workspace from Languages, Review or Translate simply moved back one
 * step and stopped.
 *
 * Here a page declares two things instead: whether it holds work that is not in
 * the database, and what to do about it. The shell asks once, gets one answer,
 * and jumps straight to the destination.
 */

/** @type {Map<string, {isDirty: () => boolean, prompt: () => object, save?: () => Promise<boolean>, leave?: () => void}>} */
const guards = new Map();

/**
 * Declare how a view behaves when the operator navigates away from it.
 *
 * @param {string} viewId
 * @param {object} guard
 * @param {() => boolean} guard.isDirty   Is there work here that is not saved?
 * @param {() => {title: string, message: string, discardText: string, saveText?: string}} guard.prompt
 *        What to ask. Omit `saveText` where saving is not possible from here —
 *        offering a Save that cannot run is worse than not offering one.
 * @param {() => Promise<boolean>} [guard.save]  Persist; resolve false to stay put.
 * @param {() => void} [guard.leave]  Reset this page's in-session state. Runs on
 *        every departure, saved or discarded, so a page is never re-entered
 *        holding the last visit's leftovers.
 */
export function registerGuard(viewId, guard) {
  guards.set(viewId, guard);
}

/**
 * Ask whether we may leave `viewId`, doing whatever the answer requires.
 *
 * @param {string} viewId
 * @param {(opts: object) => Promise<string|boolean>} confirm the app's modal
 * @returns {Promise<'go'|'stay'|'handled'>} 'go' to proceed, 'stay' to abandon
 *   the navigation, 'handled' when saving already moved the operator somewhere
 *   more useful than the original destination.
 */
export async function requestLeave(viewId, confirm) {
  const guard = guards.get(viewId);
  if (!guard) return 'go';

  if (!guard.isDirty()) {
    guard.leave?.();
    return 'go';
  }

  const { title, message, discardText, saveText } = guard.prompt();
  const canSave = !!saveText && typeof guard.save === 'function';

  // Two answers when there is nothing to save, three when there is. The
  // discarding answer is never the default action of a dismissed dialog.
  const answer = await confirm({
    title,
    message,
    confirmText: canSave ? saveText : discardText,
    altText: canSave ? discardText : '',
    cancelText: 'Stay on this page',
    variant: 'warning',
  });

  if (answer === 'cancel' || answer === false) return 'stay';

  if (canSave && answer === 'confirm') {
    // Saving here means running this page's own primary action, which finishes
    // the task and lands on its confirmation page. That is a better place to
    // end up than the button the operator happened to click, so the navigation
    // that asked the question stands down.
    const saved = await guard.save();
    return saved ? 'handled' : 'stay';
  }

  guard.leave?.();
  return 'go';
}

/** True when this view holds work that is not in the database. */
export function isViewDirty(viewId) {
  const guard = guards.get(viewId);
  return !!guard && guard.isDirty();
}
